import { useEffect, useState } from 'react';
import {
    CanadaDealerAddress,
    DealerStreetAddress,
    SelectOpt,
    USDealerAddress
} from '../CommonTypes';
import SelectInput from '../selectInput/SelectInput';
import { useTranslation } from 'react-i18next';
import { DealerApi } from 'api/DealerApi';
import { useAuth0 } from '@auth0/auth0-react';

export type DealerSelectionProps = {
    countryCode: string;
    stateCode: string;
    stateDealer: string;
    onChangeDealer: (
        dealerAddressId: string,
        dealerAddress: DealerStreetAddress
    ) => void;
};

const DealerSelection = ({
    countryCode,
    stateCode,
    stateDealer,
    onChangeDealer
}: DealerSelectionProps): JSX.Element => {
    const { getAccessTokenSilently } = useAuth0();
    const [dealersData, setDealersData] = useState<DealerStreetAddress[]>([]);

    const { t } = useTranslation();

    const populateStateDealers = async () => {
        const successCallback = (result: USDealerAddress[]) => {
            setDealersData(
                result.map((s) => {
                    return {
                        dealerId: s.dealerId,
                        city: s.city,
                        dealerName: s.dealerName,
                        stateOrProvinceCode: s.stateCode,
                        zipOrPostalCode: s.zipCode,
                        phone: s.phone,
                        street: s.street
                    } as DealerStreetAddress;
                })
            );
        };

        const failureCallback = () => {
            console.log('Failed to load dealers');
        };

        const token = await getAccessTokenSilently();

        const controller = new AbortController();

        new DealerApi().getStateDealers(
            token,
            successCallback,
            failureCallback,
            controller.signal
        );

        return () => {
            controller.abort();
        };
    };

    const populateProvinceDealers = async () => {
        const successCallback = (result: CanadaDealerAddress[]) => {
            setDealersData(
                result.map((s) => {
                    return {
                        dealerId: s.dealerId,
                        city: s.city,
                        dealerName: s.dealerName,
                        stateOrProvinceCode: s.provinceCode,
                        zipOrPostalCode: s.postalCode,
                        phone: s.phone,
                        street: s.street
                    } as DealerStreetAddress;
                })
            );
        };

        const failureCallback = () => {
            console.log('Failed to load dealers');
        };

        const token = await getAccessTokenSilently();

        const controller = new AbortController();

        new DealerApi().getProvinceDealers(
            token,
            successCallback,
            failureCallback,
            controller.signal
        );

        return () => {
            controller.abort();
        };
    };
    useEffect(() => {
        const isUnitedStates = countryCode === 'United States';
        const isCanada = countryCode === 'Canada';
        if (isUnitedStates) populateStateDealers();
        if (isCanada) populateProvinceDealers();
    }, [countryCode]);

    const stateDealers = dealersData
        .filter((d) => d.stateOrProvinceCode === stateCode)
        .map((s) => {
            return {
                id: s.dealerId.toString(),
                text: s.city + ' - ' + s.dealerName,
                value: s.dealerId.toString(),
                disabled: false
            } as SelectOpt;
        });

    const onChangeStateDealers = (selectedValue: string): void => {
        const dealerId = selectedValue;
        const dealerAddress = dealersData.find(
            (d) => d.dealerId.toString() === dealerId
        );
        onChangeDealer(dealerId, dealerAddress);
    };

    return (
        <>
            {stateDealers.length > 0 && (
                <SelectInput
                    name='location-state'
                    className='state-location-select-padding '
                    loadingText={t('data_not_found')}
                    label={t('select_dealer')}
                    value={stateDealer}
                    options={stateDealers}
                    onChange={onChangeStateDealers}
                    forceDefault
                    defaultIfOneOption
                    addDefaultOption
                    defaultText={t('select_default')}
                />
            )}

            {stateDealers.length == 0 && (
                <div
                    className='alert alert-warning panel-selection-alert-container'
                    role='alert'>
                    {t('dealer_not_available')}
                </div>
            )}
        </>
    );
};

export default DealerSelection;
